import { Link, Navigate } from 'react-router-dom';
import { BookOpenText, Carrot, Store, UtensilsCrossed } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/utils';
import { RecipeLibrarySection } from '@/components/admin/library/RecipeLibrarySection';
import { IngredientLibrarySection } from '@/components/admin/library/IngredientLibrarySection';
import { VendorLibrarySection } from '@/components/admin/library/VendorLibrarySection';
import { MobileSegmentedTabs } from '@/components/admin/library/MobileSegmentedTabs';

export type KitchenLibrarySectionKey = 'recipes' | 'ingredients' | 'vendors';

interface KitchenLibraryProps {
  section: KitchenLibrarySectionKey;
}

const libraryTabs = [
  {
    key: 'recipes',
    label: 'Recipes',
    href: '/admin/library/recipes',
    icon: UtensilsCrossed,
    description: 'Dish metadata, prep notes, and linked ingredients',
    adminOnly: false,
  },
  {
    key: 'ingredients',
    label: 'Ingredients',
    href: '/admin/library/ingredients',
    icon: Carrot,
    description: 'Core ingredient records and procurement vendor links',
    adminOnly: false,
  },
  {
    key: 'vendors',
    label: 'Vendors',
    href: '/admin/library/vendors',
    icon: Store,
    description: 'Admin-only vendor catalog used by ingredient sourcing',
    adminOnly: true,
  },
] as const;

export function KitchenLibrary({ section }: KitchenLibraryProps) {
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'ADMIN';

  if (section === 'vendors' && !isAdmin) {
    return <Navigate to="/admin/library/recipes" replace />;
  }

  const visibleTabs = libraryTabs.filter((tab) => !tab.adminOnly || isAdmin);
  const activeTab = visibleTabs.find((tab) => tab.key === section) ?? visibleTabs[0] ?? libraryTabs[0];

  return (
    <div className="container mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-8">
      <div className="relative overflow-hidden rounded-3xl border bg-gradient-to-br from-background via-background to-muted/40 p-6 shadow-sm md:p-8">
        <div className="pointer-events-none absolute right-0 top-0 h-40 w-40 rounded-full bg-primary/6 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-0 h-32 w-32 rounded-full bg-secondary/30 blur-3xl" />

        <div className="relative space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full border bg-background/80 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            <BookOpenText className="h-3.5 w-3.5" />
            Kitchen Library
          </div>

          <MobileSegmentedTabs items={visibleTabs} activeKey={activeTab.key} />

          <div className="hidden gap-3 md:grid" style={{ gridTemplateColumns: `repeat(${visibleTabs.length}, minmax(0, 1fr))` }}>
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = tab.key === activeTab.key;

              return (
                <Link
                  key={tab.key}
                  to={tab.href}
                  className={cn(
                    'group rounded-2xl border px-4 py-4 transition-colors',
                    isActive
                      ? 'border-primary/40 bg-primary/5'
                      : 'bg-background/80 hover:bg-muted/60'
                  )}
                >
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl border bg-background text-muted-foreground shadow-sm">
                      <Icon className="h-4 w-4" />
                    </div>
                    {isActive ? (
                      <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-primary">
                        Open
                      </span>
                    ) : null}
                  </div>

                  <p className="font-medium text-foreground">{tab.label}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{tab.description}</p>
                </Link>
              );
            })}
          </div>

          {section === 'recipes' ? <RecipeLibrarySection /> : null}
          {section === 'ingredients' ? <IngredientLibrarySection /> : null}
          {section === 'vendors' ? <VendorLibrarySection /> : null}
        </div>
      </div>
    </div>
  );
}
